import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useContext, useEffect, useState } from 'react'

import {
  DrinkCounterContext,
  DrinkCounterContextInterface,
} from '../../../providers/DrinkCounter/DrinkCounterProvider'
import { MUMRecordObj } from '../../../providers/MUM/MUMProvider'
import { BACObj } from '../../../providers/PatientChartPage/BACProvider'
import {
  PatientChartPageContext,
  PatientChartPageContextInterface,
} from '../../../providers/PatientChartPage/PatientChartPage.provider'
import {
  SubstanceCounterContext,
  SubstanceCounterContextInterface,
  TrackedSubstanceObj,
} from '../../../providers/SubstanceCounter/SubstanceCounter.Provider'
import { themeColors } from '../../../theme'
import { dateFormatters } from '../../../utilities/HelperFunctions'
import { useStyles } from '../PatientChartPage.styles'
import { PatientResourceCheck } from '../PatientResourceCheck'
import { CreateTrackedSubstanceModal } from './CreateTrackedSubstanceModal'
import { DiscontinueTrackedSubstanceModal } from './DiscontinueTrackedSubstanceModal'
import { LamictalScheduleReview } from './LamictalScheduleReview'
import { PassportTable } from './PassportTable'
import {
  QuestionnaireGroupSummary,
  QuestionnaireList,
} from './QuestionnaireList'
import { QuestionnaireManagement } from './QuestionnaireManagement'
import { ReviewBacMumTable } from './ReviewBacMumTable'
import { ReviewBacOrDrinkTypesModal } from './ReviewBacOrDrinkTypesModal'

interface MyJourneyReviewTableInterface {
  routedPatientId: string | undefined
  selectedReviewOption: string
  ReviewBACTableTabs: string[]
  ReviewMUMTableTabs: string[]
  videoReviewFilter: boolean
  setVideoReviewFilter: React.Dispatch<React.SetStateAction<boolean>>
  filteredBacWithVideos: BACObj[]
  setFilteredBacWithVideos: React.Dispatch<React.SetStateAction<BACObj[]>>
  filteredMUMVideos: MUMRecordObj[]
  setFilteredMUMVideos: React.Dispatch<React.SetStateAction<MUMRecordObj[]>>
}

export const MyJourneyReviewTable = ({
  routedPatientId,
  selectedReviewOption,
  ReviewBACTableTabs,
  ReviewMUMTableTabs,
  videoReviewFilter,
  setVideoReviewFilter,
  filteredBacWithVideos,
  setFilteredBacWithVideos,
  filteredMUMVideos,
  setFilteredMUMVideos,
}: MyJourneyReviewTableInterface) => {
  const { classes } = useStyles()

  const { patientDrinkTypes, getPatientDrinkTypes } = useContext(
    DrinkCounterContext
  ) as DrinkCounterContextInterface

  const { activeChartPatient, registerForDrugScreens } = useContext(
    PatientChartPageContext
  ) as PatientChartPageContextInterface

  const { patientTrackedSubstances, getPatientTrackedSubstancesForProvider } =
    useContext(SubstanceCounterContext) as SubstanceCounterContextInterface

  const [showReviewModal, setShowReviewModal] = useState(false)

  const [showCreateTrackedSubstanceModal, setShowCreateTrackedSubstanceModal] =
    useState(false)
  const [showDiscontinueModal, setShowDiscontinueModal] = useState(false)
  const [trackedSubstanceDiscontinueId, setTrackedSubstanceDiscontinueId] =
    useState(0)

  const [selectedQuestionnaireSummary, setSelectedQuestionnaireSummary] =
    useState<QuestionnaireGroupSummary>({
      questionnaireId: 0,
      title: '',
      activeAssignment: false,
      lastSubmissionDate: '',
      totalSubmissions: 0,
    })

  const [drugScreenDialogOpen, setDrugScreenDialogOpen] = useState(false)

  const isRegistered =
    activeChartPatient.drugScreenChartData?.needsRandomDrugScreens

  const handleOpenDrugScreenDialog = () => {
    setDrugScreenDialogOpen(true)
  }

  const handleCloseDrugScreenDialog = () => {
    if (drugScreenRegistering) return

    setDrugScreenDialogOpen(false)
  }

  const handleConfirmDrugScreenRegistration = async () => {
    await handleDrugScreenRegistration()
    setDrugScreenDialogOpen(false)
  }

  const [drugScreenFrequency, setDrugScreenFrequency] = useState<
    'Weekly' | 'Monthly'
  >('Weekly')

  const [drugScreenRegistering, setDrugScreenRegistering] =
    useState<boolean>(false)

  const [
    openChangeDrugScreenFrequencyDialog,
    setOpenChangeDrugScreenFrequencyDialog,
  ] = useState(false)

  const [drugScreenFrequencyChanging, setDrugScreenFrequencyChanging] =
    useState<boolean>(false)

  const handleDrugScreenRegistration = async () => {
    setDrugScreenRegistering(true)

    // if needsRandomDrugScreens was set true previously, means user is ending it now
    const registering =
      !activeChartPatient.drugScreenChartData?.needsRandomDrugScreens

    // starting registration
    if (registering) {
      if (!drugScreenFrequency) {
        return (
          <Alert sx={{ mb: 2 }}>
            Please make sure you select the drug screen frequency before
            registration.
          </Alert>
        )
      }
      await registerForDrugScreens(
        activeChartPatient.patientId,
        true,
        drugScreenFrequency
      )
    } else {
      // ending registration
      await registerForDrugScreens(
        activeChartPatient.patientId,
        false,
        undefined
      )
    }

    setDrugScreenRegistering(false)
  }

  const handleChangeDrugScreenFrequency = async () => {
    if (!drugScreenFrequency) return

    if (
      drugScreenFrequency === activeChartPatient.drugScreenChartData?.frequency
    ) {
      alert(
        `This patient is already registered for ${drugScreenFrequency} random drug screens. Please select a different frequency.`
      )
      return
    }

    setDrugScreenFrequencyChanging(true)

    await registerForDrugScreens(
      activeChartPatient.patientId,
      true,
      drugScreenFrequency
    )
    setDrugScreenFrequencyChanging(false)

    setOpenChangeDrugScreenFrequencyDialog(false)
  }

  useEffect(() => {
    let isMounted = true

    const fetchData = async () => {
      if (isMounted) {
        await getPatientDrinkTypes(Number(routedPatientId))
        await getPatientTrackedSubstancesForProvider(Number(routedPatientId))
      }
    }
    fetchData()

    return () => {
      isMounted = false
    }
  }, [routedPatientId])

  const calculateDrugScreenScheduledDate = () => {
    if (activeChartPatient.drugScreenChartData?.drugScreenDate) {
      const date = dayjs(
        activeChartPatient.drugScreenChartData?.drugScreenDate
      ).format('MM/DD/YYYY')

      if (date === '01/01/1901') {
        return 'Date will be assigned Sunday Evening'
      }

      return date
    }
    return 'N/A'
  }

  if (
    selectedReviewOption === 'Review MUM' &&
    filteredMUMVideos.some((v) => v.videoURL)
  ) {
    return (
      <ReviewBacMumTable
        routedPatientId={routedPatientId}
        selectedReviewOption={selectedReviewOption}
        reviewTableTabs={ReviewMUMTableTabs}
        filteredMUMVideos={filteredMUMVideos}
        videoReviewFilter={videoReviewFilter}
        setVideoReviewFilter={setVideoReviewFilter}
        setFilteredMUMVideos={setFilteredMUMVideos}
        showReviewModal={showReviewModal}
        setShowReviewModal={setShowReviewModal}
      />
    )
  }
  if (
    selectedReviewOption === 'Review BAC' &&
    filteredBacWithVideos?.some((v) => v.videoURL)
  ) {
    return (
      <ReviewBacMumTable
        routedPatientId={routedPatientId}
        selectedReviewOption={selectedReviewOption}
        reviewTableTabs={ReviewBACTableTabs}
        filteredBacWithVideos={filteredBacWithVideos}
        videoReviewFilter={videoReviewFilter}
        setVideoReviewFilter={setVideoReviewFilter}
        setFilteredBacWithVideos={setFilteredBacWithVideos}
        showReviewModal={showReviewModal}
        setShowReviewModal={setShowReviewModal}
      />
    )
  }
  if (
    selectedReviewOption === 'Review Drink Types' &&
    patientDrinkTypes?.some((r) => r.name)
  ) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <table className={classes.reviewTable}>
          <thead className={classes.tableThead}>
            <tr>
              <th className={classes.tableLabelTh} scope='col'>
                Drink types
              </th>
              <th className={classes.tableLabelTh} scope='col'>
                Alcohol Content (%)
              </th>
              <th className={classes.tableLabelTh} scope='col'>
                Drink Ounces
              </th>
            </tr>
          </thead>

          <tbody>
            {patientDrinkTypes?.map((t, i) => {
              return (
                <tr key={`table-drinkType-${i}`}>
                  <th className={classes.tableTh} scope='col'>
                    {t.name}
                  </th>
                  <th className={classes.tableTh} scope='col'>
                    {t.alcoholContent}
                  </th>
                  <th className={classes.tableTh} scope='col'>
                    {t.drinkOunces}
                  </th>
                </tr>
              )
            })}

            <ReviewBacOrDrinkTypesModal
              routedPatientId={routedPatientId}
              selectedReviewOption={selectedReviewOption}
              showReviewModal={showReviewModal}
              setShowReviewModal={setShowReviewModal}
            />
          </tbody>
        </table>

        <Button
          onClick={() => {
            setShowReviewModal(true)
          }}
          variant='contained'
          style={{
            fontSize: '16px',
            marginTop: '10px',
            width: '170px',
            backgroundColor: themeColors.red,
            borderColor: 'transparent',
            borderRadius: '5px',
            alignSelf: 'flex-end',
          }}
        >
          Reset Drink Types
        </Button>
      </div>
    )
  }
  if (selectedReviewOption === 'Resources') {
    return <PatientResourceCheck routedPatientId={routedPatientId} />
  }
  if (selectedReviewOption === 'Review Substance Counter') {
    const currentPatientTrackedSubstances = patientTrackedSubstances.filter(
      (p: TrackedSubstanceObj) => p.isActive
    )

    const historyPatientTrackedSubstances = patientTrackedSubstances.filter(
      (p: TrackedSubstanceObj) => !p.isActive
    )

    const renderMedicationTable = (
      groupedPatientTrackedSubstances: TrackedSubstanceObj[]
    ) => {
      return (
        <table className={classes.reviewTable}>
          <thead className={classes.tableThead}>
            <tr>
              <th className={classes.tableLabelTh} scope='col'>
                Substance
              </th>
              <th className={classes.tableLabelTh} scope='col'>
                Drug Form
              </th>
              <th className={classes.tableLabelTh} scope='col'>
                Dosage Unit
              </th>
              <th className={classes.tableLabelTh} scope='col'>
                Start Date
              </th>
              <th className={classes.tableLabelTh} scope='col'>
                Discontinue Date
              </th>
              <th className={classes.tableLabelTh} scope='col'></th>
            </tr>
          </thead>

          <tbody>
            {groupedPatientTrackedSubstances.map(
              (t: TrackedSubstanceObj, i) => {
                return (
                  <tr key={`table-drinkType-${i}`}>
                    <th className={classes.tableTh} scope='col'>
                      {t.substance?.name}
                    </th>

                    <th className={classes.tableTh} scope='col'>
                      {t.drugForm?.name}
                    </th>

                    <th className={classes.tableTh} scope='col'>
                      {t.dosageUnit?.unitName}
                    </th>

                    <th className={classes.tableTh} scope='col'>
                      {dayjs(t.createdAt).format('MM/DD/YYYY')}
                    </th>

                    <th className={classes.tableTh} scope='col'>
                      <Button
                        variant={t.isActive ? 'contained' : 'text'}
                        onClick={() => {
                          if (t.id) {
                            setTrackedSubstanceDiscontinueId(t.id)
                            setShowDiscontinueModal(true)
                          }
                        }}
                        disabled={!t.isActive}
                        sx={{
                          alignItems: 'center',
                          fontSize: '14px',
                        }}
                      >
                        {t.isActive ? <DeleteOutlineIcon /> : null}

                        {t.isActive ? (
                          'Discontinue'
                        ) : t.modifiedAt ? (
                          <Typography color={themeColors.gray40} fontSize={13}>
                            {dateFormatters.prettyDate(t.modifiedAt)}
                          </Typography>
                        ) : null}
                      </Button>
                    </th>
                  </tr>
                )
              }
            )}
          </tbody>
        </table>
      )
    }

    return (
      <Box>
        <Button
          onClick={() => {
            setShowCreateTrackedSubstanceModal(true)
          }}
          variant='contained'
          style={{
            fontSize: '16px',
            margin: '20px 0',
            width: '270px',
          }}
        >
          Create New Tracked Substance
        </Button>

        <Typography fontWeight={700} style={{ margin: '15px 0' }}>
          Current
        </Typography>
        {currentPatientTrackedSubstances.length ? (
          renderMedicationTable(currentPatientTrackedSubstances)
        ) : (
          <Typography fontWeight={700} style={{ margin: '15px 0' }}>
            No prescription found.
          </Typography>
        )}

        <Typography fontWeight={700} style={{ margin: '25px 0 15px 0' }}>
          History
        </Typography>
        {historyPatientTrackedSubstances.length ? (
          renderMedicationTable(historyPatientTrackedSubstances)
        ) : (
          <Typography fontWeight={700} style={{ margin: '15px 0' }}>
            No prescription found.
          </Typography>
        )}

        <CreateTrackedSubstanceModal
          routedPatientId={routedPatientId}
          showCreateTrackedSubstanceModal={showCreateTrackedSubstanceModal}
          setShowCreateTrackedSubstanceModal={
            setShowCreateTrackedSubstanceModal
          }
        />

        <DiscontinueTrackedSubstanceModal
          routedPatientId={routedPatientId}
          showDiscontinueModal={showDiscontinueModal}
          setShowDiscontinueModal={setShowDiscontinueModal}
          trackedSubstanceDiscontinueId={trackedSubstanceDiscontinueId}
        />
      </Box>
    )
  }
  if (selectedReviewOption === 'Review Drug Screens') {
    return (
      <Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          justifyContent='space-between'
        >
          <Typography fontWeight={700} margin={2}>
            Patient is{' '}
            {activeChartPatient.drugScreenChartData?.needsRandomDrugScreens ? (
              ''
            ) : (
              <strong>NOT </strong>
            )}
            registered for{' '}
            {activeChartPatient.drugScreenChartData?.frequency ? (
              <strong>
                {activeChartPatient.drugScreenChartData?.frequency}
              </strong>
            ) : (
              ''
            )}{' '}
            Random Drug Screens.
          </Typography>
        </Box>

        <Box
          display={'flex'}
          flexDirection={'row'}
          alignItems={'center'}
          marginX={2}
        >
          {activeChartPatient.drugScreenChartData?.needsRandomDrugScreens && (
            <Button
              variant='outlined'
              style={{
                fontSize: '16px',
                width: '220px',
                marginRight: 10,
              }}
              onClick={() => setOpenChangeDrugScreenFrequencyDialog(true)}
            >
              Change Frequency
            </Button>
          )}

          <Button
            variant='contained'
            color={
              activeChartPatient.drugScreenChartData?.needsRandomDrugScreens
                ? 'error'
                : 'primary'
            }
            style={{
              fontSize: '16px',
              width: '280px',
              margin: 2,
            }}
            onClick={() => handleOpenDrugScreenDialog()}
          >
            {activeChartPatient.drugScreenChartData?.needsRandomDrugScreens
              ? 'End Random Drug Screens?'
              : 'Register for Random Drug Screens'}
          </Button>
        </Box>

        {/* start/change drug screen registration  */}
        <Dialog
          open={openChangeDrugScreenFrequencyDialog}
          onClose={() => setOpenChangeDrugScreenFrequencyDialog(false)}
          fullWidth
          maxWidth='xs'
        >
          <DialogTitle>Change Drug Screen Frequency</DialogTitle>

          <DialogContent>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>Frequency</InputLabel>

              <Select
                value={drugScreenFrequency}
                label='Frequency'
                onChange={(e) =>
                  setDrugScreenFrequency(e.target.value as 'Weekly' | 'Monthly')
                }
              >
                <MenuItem value='Weekly'>Weekly</MenuItem>
                <MenuItem value='Monthly'>Monthly</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setOpenChangeDrugScreenFrequencyDialog(false)}
            >
              Cancel
            </Button>

            <Button
              variant='contained'
              onClick={handleChangeDrugScreenFrequency}
              disabled={!drugScreenFrequency}
            >
              {drugScreenFrequencyChanging ? (
                <CircularProgress size={20} color='secondary' />
              ) : (
                'Save'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* end drug screen registration  */}
        <Dialog
          open={drugScreenDialogOpen}
          onClose={handleCloseDrugScreenDialog}
          fullWidth
          maxWidth='xs'
        >
          <DialogTitle>
            {isRegistered
              ? 'End Random Drug Screens'
              : 'Register for Random Drug Screens'}
          </DialogTitle>

          <DialogContent>
            {isRegistered ? (
              <DialogContentText>
                Are you sure you want to end random drug screens for this
                patient?
              </DialogContentText>
            ) : (
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel>Frequency</InputLabel>

                <Select
                  value={drugScreenFrequency}
                  label='Frequency'
                  onChange={(e) =>
                    setDrugScreenFrequency(
                      e.target.value as 'Weekly' | 'Monthly'
                    )
                  }
                >
                  <MenuItem value='Weekly'>Weekly</MenuItem>
                  <MenuItem value='Monthly'>Monthly</MenuItem>
                </Select>
              </FormControl>
            )}
          </DialogContent>

          <DialogActions>
            <Button
              onClick={handleCloseDrugScreenDialog}
              disabled={drugScreenRegistering}
            >
              Cancel
            </Button>

            <Button
              variant='contained'
              onClick={handleConfirmDrugScreenRegistration}
              disabled={
                drugScreenRegistering || (!isRegistered && !drugScreenFrequency)
              }
            >
              {drugScreenRegistering ? (
                <CircularProgress size={20} />
              ) : isRegistered ? (
                'End Drug Screens'
              ) : (
                'Register'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {activeChartPatient.drugScreenChartData?.needsRandomDrugScreens && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <p style={{ margin: '15px 33px' }}>
              Next Scheduled Drug Screen:{' '}
              <strong>{calculateDrugScreenScheduledDate()}</strong>
            </p>

            {!activeChartPatient.drugScreenChartData.completed && (
              <p style={{ margin: '15px 33px' }}>
                Patient Scheduled For:{' '}
                <strong>
                  {activeChartPatient.drugScreenChartData.patientScheduled
                    ? dayjs(
                        activeChartPatient.drugScreenChartData?.patientScheduled
                      ).format('MM/DD/YYYY hh:mma')
                    : 'Not scheduled yet'}
                </strong>
              </p>
            )}

            <p style={{ margin: '15px 33px' }}>
              Completed:{' '}
              <strong>
                {activeChartPatient.drugScreenChartData.completed
                  ? 'Yes'
                  : 'No'}
              </strong>
            </p>
          </div>
        )}
      </Box>
    )
  }
  if (selectedReviewOption === 'Review Passport') {
    return <PassportTable patientId={routedPatientId} />
  }
  if (selectedReviewOption === 'Questionnaires' && routedPatientId) {
    if (!selectedQuestionnaireSummary.questionnaireId) {
      return (
        <QuestionnaireList
          patientId={parseInt(routedPatientId)}
          setSelectedQuestionnaireSummary={setSelectedQuestionnaireSummary}
        />
      )
    } else if (
      selectedQuestionnaireSummary.title.includes('SSRI') &&
      routedPatientId
    ) {
      return (
        <QuestionnaireManagement
          patientId={parseInt(routedPatientId)}
          questionnaireGroupSummary={selectedQuestionnaireSummary}
          recurrenceWeeks={8}
          recurrenceIntervalDays={7}
          onBack={() =>
            setSelectedQuestionnaireSummary({
              questionnaireId: 0,
              title: '',
              activeAssignment: false,
              lastSubmissionDate: '',
              totalSubmissions: 0,
            })
          }
        />
      )
    } else {
      return <Typography>Coming Soon</Typography>
    }
  }
  if (selectedReviewOption === 'Lamictal Schedule') {
    return <LamictalScheduleReview patientId={routedPatientId} />
  } else {
    return (
      <span className={classes.tableCaption}>
        This patient has no record yet.
      </span>
    )
  }
}
